import React, { useEffect, useState } from 'react';
import {
  Tabs,
  Tab,
  Box,
  Button,
  IconButton,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { buttonMarginSpace, buttonWidth } from '../constants/form';
import { useFormik } from 'formik';
import { pushToast, useDispatch, useSelector } from '../../../state';
import {
  closeModal,
  setFormData,
  setLoading,
  fetchAccount,
  updateAccount,
  changeAccountPassword,
} from '../../../state/accountModalSlice';
import DraggablePaper from '../DraggablePaper';
import { useTranslation } from 'react-i18next';
import { serverError } from '../constants/errors';
import About from './About';
import { AuthError } from 'aws-amplify/auth';
import Password from './Password';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

export interface ChangePassword {
  oldPassword: string;
  oldPasswordError: string;
  newPassword: string;
  newPasswordError: string;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

export default function AccountModal() {
  const { t: tGlobal } = useTranslation([], {
    keyPrefix: 'team.Global.Buttons',
  });
  const { t: tMA } = useTranslation([], { keyPrefix: 'team.Modals.Account' });

  const {
    stateIsChanged,
    formData,
    isOpenAccountModal,
    loading,
    disableButtons,
  } = useSelector(({ accountModalSlice }) => accountModalSlice);

  const [tab, setTab] = useState(0);
  const [changePasswordData, setChangePasswordData] = useState<ChangePassword>({
    oldPassword: '',
    oldPasswordError: '',
    newPassword: '',
    newPasswordError: '',
  });

  const dispatch = useDispatch();

  const updateLoading = (value: boolean) => {
    dispatch(setLoading(value));
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  const resetState = () => {
    setTab(0);
    formik.resetForm();
  };

  const handleClose = () => {
    dispatch(closeModal({ resetState: true }));
    resetState();
  };

  const handleSave = async () => {
    updateLoading(true);
    const [account, error] = await dispatch(
      updateAccount({
        userAttributes: {
          name: formik.values.firstName,
          family_name: formik.values.lastName,
        },
      }),
    );
    if (error) {
      if (error instanceof AuthError) {
        dispatch(pushToast({ message: error.message, type: 'error' }));
      } else {
        console.log('error: ', error);
        dispatch(pushToast({ message: serverError, type: 'error' }));
      }
      handleClose();
    }
    dispatch(
      pushToast({
        message: tMA('Account information updated'),
        type: 'success',
      }),
    );
    handleClose();
    updateLoading(false);
  };

  const handleChangePassword = async () => {
    updateLoading(true);
    // --- OLD PASSWORD VALIDATION ---
    if (changePasswordData.oldPassword.trim() === '') {
      setChangePasswordData({
        ...changePasswordData,
        oldPasswordError: 'this field is required',
      });
      updateLoading(false);
      return;
    }
    // --- PASSWORD VALIDATION ---
    const password = changePasswordData.newPassword.trim();
    /*
            // This is cognito requirements that I found on https://docs.aws.amazon.com/cognito/latest/developerguide/managing-users-passwords.html
            Length 8–256
            At least one lowercase
            At least one uppercase
            At least one digit
            At least one special from Cognito’s allowed set
        */
    const cognitoPasswordRegex =
      /^(?!\s)(?!.*\s$)(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^\w\s]).{8,256}$/;

    if (!cognitoPasswordRegex.test(password)) {
      setChangePasswordData({
        ...changePasswordData,
        newPasswordError: tMA(
          'Password must be 8–256 characters and include at least one uppercase letter, one lowercase letter, one number, and one special character.',
        ),
      });
      updateLoading(false);
      return;
    }

    const error = await dispatch(
      changeAccountPassword({
        oldPassword: changePasswordData.oldPassword,
        newPassword: changePasswordData.newPassword,
      }),
    );
    if (error) {
      if (error instanceof AuthError) {
        dispatch(pushToast({ message: error.message, type: 'error' }));
      } else {
        console.log('error: ', error);
        dispatch(pushToast({ message: serverError, type: 'error' }));
      }
      updateLoading(false);
      return;
    }
    dispatch(pushToast({ message: tMA('Password changed'), type: 'success' }));
    handleClose();
    updateLoading(false);
  };

  // Use formData from redux as initialValues
  const formik = useFormik({
    initialValues: formData,
    enableReinitialize: false, // Do not Reinitialize form values when formData in redux changes
    onSubmit: handleSave,
  });

  // Sync formik values back to Redux when they change
  useEffect(() => {
    dispatch(setFormData(formik.values));
  }, [formik.values]);

  const fetchAccountData = async () => {
    const [account, error] = await dispatch(fetchAccount());
    formik.setValues({
      email: account?.email || '',
      firstName: account?.name || '',
      lastName: account?.family_name || '',
    });
    if (error) {
      if (error instanceof AuthError) {
        dispatch(pushToast({ message: error.message, type: 'error' }));
      } else {
        console.log('error: ', error);
        dispatch(pushToast({ message: serverError, type: 'error' }));
      }
      handleClose();
    }
  };

  useEffect(() => {
    // Here we don't make api calls if modal sate not changed (came back from other modal)
    // We also need to chek if its in edit mode because the state will be changed in it but we still need to fetch
    if (!isOpenAccountModal) return;
    if (stateIsChanged) return;
    const fetchData = async () => {
      updateLoading(true);
      await Promise.all([fetchAccountData()]);
      updateLoading(false);
    };
    // Getting Initial data from formData (redux)
    formik.setValues(formData);
    fetchData();
  }, [isOpenAccountModal]);

  return (
    <Dialog
      open={isOpenAccountModal}
      onClose={(event, reason) => {
        if (reason && reason === 'backdropClick') return;
        handleClose();
      }}
      maxWidth="sm"
      scroll="paper"
      PaperProps={{ className: 'w-full' }}
      PaperComponent={DraggablePaper}
    >
      <DialogTitle
        id="draggable-dialog-title"
        sx={{ m: 0, p: 1, cursor: 'move' }}
      >
        {tMA('Account')}
      </DialogTitle>
      <IconButton
        disabled={loading || disableButtons}
        color="primary"
        sx={{ position: 'absolute', right: 4, top: 4 }}
        onClick={handleClose}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers sx={{ p: 1 }}>
        {loading ? (
          <Box
            height="400px"
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <CircularProgress size={80} />
          </Box>
        ) : (
          <form onSubmit={formik.handleSubmit}>
            <Container>
              <Tabs
                centered
                value={tab}
                onChange={handleTabChange}
                sx={{
                  '& .MuiTabs-flexContainer': {
                    justifyContent: 'space-around',
                  },
                }}
              >
                <Tab
                  sx={{ fontWeight: 'bold' }}
                  label={tMA('About')}
                  {...a11yProps(0)}
                />
                <Tab
                  sx={{ fontWeight: 'bold' }}
                  label={tMA('Password')}
                  {...a11yProps(1)}
                />
              </Tabs>
            </Container>
            <TabPanel value={tab} index={0}>
              <About formik={formik} />
            </TabPanel>
            <TabPanel value={tab} index={1}>
              <Password
                changePasswordData={changePasswordData}
                setChangePasswordData={setChangePasswordData}
              />
            </TabPanel>
          </form>
        )}
      </DialogContent>
      <DialogActions>
        <Box width="100%" sx={{ display: 'flex', justifyContent: 'end' }}>
          <Button
            disabled={loading || disableButtons}
            variant="outlined"
            color="primary"
            sx={{ width: buttonWidth, marginRight: buttonMarginSpace }}
            onClick={handleClose}
          >
            {tGlobal('Cancel')}
          </Button>
          {tab === 0 ? (
            <Button
              disabled={loading || disableButtons}
              variant="contained"
              color="primary"
              sx={{ width: buttonWidth }}
              onClick={() => {
                formik.submitForm();
              }}
            >
              {loading || disableButtons ? (
                <CircularProgress size={20} />
              ) : (
                tGlobal('Save')
              )}
            </Button>
          ) : (
            <Button
              disabled={loading || disableButtons}
              variant="contained"
              color="primary"
              sx={{ width: '190px' }}
              onClick={() => {
                handleChangePassword();
              }}
            >
              {loading || disableButtons ? (
                <CircularProgress size={20} />
              ) : (
                tMA('Change Password')
              )}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
}
